import { useState } from "react";
import { MapPin } from "lucide-react";
import { Reveal, Chapter } from "@/components/Reveal";
import { MARKETS } from "@/data/content";

const US_OUTLINE =
  "20,40 38,95 27,200 24,250 83,388 133,435 171,438 227,465 297,455 333,518 373,505 407,590 440,603 437,555 478,518 526,520 560,520 578,495 618,505 657,550 684,620 692,672 695,653 681,580 687,486 753,413 762,400 756,354 791,257 823,240 839,224 841,189 848,170 869,154 896,135 884,81 779,135 715,180 662,224 627,95 594,84 554,54 518,40 469,27 439,27 336,27 230,27 155,27 49,30";

export const ServiceArea = () => {
  const [active, setActive] = useState(MARKETS[0]);

  return (
    <section className="relative overflow-hidden bg-charcoal/30 py-24 lg:py-32" data-testid="service-area-section">
      <div className="container-x">
        <Reveal>
          <Chapter index="08" label="Service Area" />
          <div className="grid items-end gap-6 lg:grid-cols-[1.5fr_1fr]">
            <h2 className="font-display text-2xl font-bold tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
              Reviewing Qualified Operators <span className="text-aqua">Across the United States</span>
            </h2>
            <p className="text-sm leading-relaxed text-muted lg:text-right">
              Expansion is focused on major coastal and recreational-lake markets. Markets shown are under review —
              no active deployment is implied until a Fleet Partner is approved and announced.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid items-center gap-10 lg:grid-cols-[1.6fr_1fr]">
          <Reveal>
            <div className="card-line relative rounded-2xl p-4 sm:p-8" data-testid="service-area-map">
              <svg viewBox="0 0 1000 680" className="h-auto w-full" role="img" aria-label="Stylized map of the United States showing markets under review">
                <polygon
                  points={US_OUTLINE}
                  fill="rgba(0,252,245,0.03)"
                  stroke="rgba(0,252,245,0.35)"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                {MARKETS.map((m) => (
                  <g key={m.id}>
                    <circle
                      cx={m.x}
                      cy={m.y}
                      r={active.id === m.id ? 16 : 10}
                      fill="none"
                      stroke="rgba(0,252,245,0.35)"
                      strokeWidth="1.5"
                      className={active.id === m.id ? "animate-ping [transform-origin:center]" : ""}
                      style={{ transformBox: "fill-box", transformOrigin: "center" }}
                    />
                    <circle
                      cx={m.x}
                      cy={m.y}
                      r={active.id === m.id ? 8 : 5.5}
                      fill={active.id === m.id ? "#00FCF5" : "#171B1E"}
                      stroke="#00FCF5"
                      strokeWidth="2"
                      className="cursor-pointer transition-all duration-300 hover:fill-[#00FCF5]"
                      onClick={() => setActive(m)}
                      data-testid={`map-pin-${m.id}`}
                    >
                      <title>{m.name}</title>
                    </circle>
                  </g>
                ))}
              </svg>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="card-line rounded-2xl p-8" data-testid="market-detail-panel">
              <p className="field-label">Market under review</p>
              <h3 className="flex items-center gap-2.5 font-display text-xl font-bold text-offwhite" data-testid="market-detail-name">
                <MapPin size={18} className="shrink-0 text-aqua" /> {active.name}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-muted">
                Xtreme Exotics is reviewing qualified waterfront operators in this market. If your business operates
                here, an approved application is the path to deployment.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {MARKETS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setActive(m)}
                    data-testid={`market-chip-${m.id}`}
                    className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors duration-300 ${
                      active.id === m.id
                        ? "border-aqua bg-aqua/15 text-aqua"
                        : "border-black/10 text-muted hover:border-aqua/40 hover:text-offwhite"
                    }`}
                  >
                    {m.name.split(" / ")[0].split(",")[0]}
                  </button>
                ))}
              </div>
              <p className="mt-6 text-[11px] leading-relaxed text-muted/70">
                This map is updated as Fleet Partners are approved. Presence of a market does not indicate a current
                deployment.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
